import { useCallback } from 'react';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/core';
import { useClientTranslation } from '@/lib/core/i18n';

export interface HeartToggleProps {
  isFavorited: boolean;
  onToggle: (next: boolean) => void;
  /** Icon size in px. Defaults to 16. */
  size?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * Toggle button rendering an outline / solid heart from Iconoir.
 * - `aria-pressed` reflects the favourited state.
 * - Tapping flips the state and notifies the parent (no internal state).
 */
export function HeartToggle({ isFavorited, onToggle, size = 24, className, disabled }: HeartToggleProps) {
  const t = useClientTranslation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      onToggle(!isFavorited);
    },
    [disabled, isFavorited, onToggle]
  );

  return (
    <button
      type="button"
      aria-pressed={isFavorited}
      aria-label={isFavorited ? t('App.Shop.RemoveFromWishlist') : t('App.Shop.AddToWishlist')}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'text-jncquoi-neutral-1000 inline-flex items-center justify-center',
        'disabled:cursor-not-allowed disabled:opacity-40',
        className
      )}
    >
      <Icon name={isFavorited ? 'heart-solid' : 'heart'} width={size} height={size} />
    </button>
  );
}
